import * as z from "zod";
import { userFacingError } from "../authorization";
import { supportsGitAuth } from "../connectors/gitAuthIdentifiers";
import { matchesUrlPattern } from "../connectors/matchesUrlPattern";
import { isFeatureFlagEnabled } from "../featureFlags";
import { GIT_SYNC_FEATURE_FLAG } from "../featureFlagNames";
import {
  dropConnectorUsage,
  recordConnectorUsage,
  usageRef,
} from "../connectorUsages";
import { zql } from "../generatedDrizzleSchema";
import { assertSupplierAvailable } from "../rbac/crossResourceAvailability";
import { mutator } from "./mutator";

export const configureGitSyncSchema = z.object({
  spaceId: z.string(),
  remoteUrl: z.string().url().startsWith("https://"),
  connectedAppId: z.string(),
});

export const configureGitSync = mutator({
  schema: configureGitSyncSchema,
  resolve: {
    space: { kind: "space", from: "spaceId" },
    app: { kind: "connectedApp", from: "connectedAppId" },
  },
  permissions: [
    {
      permission: "space:manage",
      scopeId: ({ args }) => args.space.id,
    },
    {
      permission: "connected_app:use",
      scopeId: ({ args }) => args.app.id,
    },
  ],
  fn: async ({ tx, ctx, args: { space, app, remoteUrl } }) => {
    if (
      !(await isFeatureFlagEnabled(tx, GIT_SYNC_FEATURE_FLAG, ctx.tenantId))
    ) {
      throw userFacingError("Git sync is not available");
    }
    if (!supportsGitAuth(app.identifier)) {
      throw userFacingError("Connector does not support git sync");
    }

    await assertSupplierAvailable({
      tx,
      tenantId: ctx.tenantId,
      ruleId: "space_uses_connected_apps",
      dependentScopeId: space.id,
      supplierScopeId: app.id,
    });

    const parsedRemote = new URL(remoteUrl);
    if (parsedRemote.username || parsedRemote.password) {
      throw userFacingError(
        "Repository URLs must not contain embedded credentials"
      );
    }
    if (!matchesUrlPattern(app.urlPattern, remoteUrl)) {
      throw userFacingError(
        "Repository URL must match the connector’s URL pattern"
      );
    }

    const ref = usageRef.spaceGitRemote(space.id);
    const [existingUsage] = await tx.run(
      zql.connectedAppUsages.where("type", ref.type).where("refId", ref.refId)
    );
    if (existingUsage && existingUsage.connectedAppId !== app.id) {
      await dropConnectorUsage(tx, existingUsage.connectedAppId, ref);
    }
    await tx.mutate.spaceGitRemotes.upsert({
      spaceId: space.id,
      remoteUrl,
      lastError: null,
      lastSyncedAt: null,
      syncStartedAt: null,
      webhookReceivedAt: null,
      tenantId: ctx.tenantId,
    });
    if (!existingUsage || existingUsage.connectedAppId !== app.id) {
      await recordConnectorUsage(tx, app.id, ref, ctx.tenantId);
    }
  },
});
